/**
 * Campana de notificaciones
 * -----------------------------------------------------------------------------
 * Va en la barra superior, junto al menú de usuario, y es la misma en todas las
 * apps de la suite.
 *
 *   const campana = CompanyTheme.mountNotificationBell('#navbar-actions', {
 *     items: [
 *       { id: 1, title: 'Factura aprobada', text: 'La FV-1042 pasó a aprobada',
 *         time: 'hace 5 min', href: '/facturas/1042' },
 *     ],
 *     onRead: (item) => marcarLeida(item.id),
 *   });
 *
 *   campana.add({ id: 7, title: 'Nuevo pedido', text: '…' });   // llega una
 *   campana.markAllRead();
 *
 * MDB pone la pieza del badge (`.badge-notification`) y el desplegable; la
 * lista, el contador y el estado de leído son de aquí, porque no los trae.
 *
 * No confundir con `notify()`, que son los avisos que aparecen y se van solos
 * en una esquina. Esto es el historial: se queda hasta que se lee.
 *
 * El theme NO consulta nada ni sabe de dónde salen las notificaciones. La
 * aplicación las pasa al montar y llama a `add()` cuando llegan —por WebSocket,
 * por sondeo, como sea—.
 */

import { notificationBellES } from './locale-es.js';
import { initMdbOn } from './mdb-init.js';
import { normalizeText } from './normalize-text.js';

let mdbComponents = null;

/** Recibe los componentes de MDB. Lo llama `autoInit()`. */
export function setBellComponents(components = {}) {
  mdbComponents = components;
}

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

/**
 * Una notificación se compone de:
 *
 *   id      obligatorio. Identifica la notificación para marcarla leída y para
 *           no repetirla si llega dos veces.
 *   title   obligatorio. La línea que se lee de un vistazo.
 *   text    opcional. El detalle, en gris y más pequeño.
 *   time    opcional. Lo que se ve: «hace 5 min», «ayer». Texto libre, porque
 *           quien sabe formatear fechas relativas en el idioma del usuario es
 *           la aplicación, no el theme.
 *   date    opcional. Fecha real en ISO. NO se enseña: sirve para agrupar por
 *           día en la vista completa y para ordenar.
 *   icon    opcional. Clase de Font Awesome; por defecto un círculo de info.
 *   type    opcional. `info` (por defecto), `success`, `warning` o `danger`.
 *           Solo tiñe el icono.
 *   href    opcional. A dónde lleva la notificación. NO se pinta como `href` en
 *           el marcado —la fila siempre es un botón—: se guarda y solo se usa
 *           cuando el servidor confirma que quedó leída. Sin él, pulsarla solo
 *           la marca.
 *   read    opcional. `true` si ya se leyó.
 */
const ICON_BY_TYPE = {
  info: 'fa-circle-info',
  success: 'fa-circle-check',
  warning: 'fa-triangle-exclamation',
  danger: 'fa-circle-exclamation',
};

/**
 * Pinta una notificación. La comparten la campana y la vista completa, para que
 * no haya dos maneras de dibujar lo mismo que se desincronicen.
 *
 * @param {Object} item
 * @param {Function} onRead Se llama al pulsarla.
 * @param {string} [prefix='company-bell'] Familia de clases.
 */
export function renderNotification(item, onActivate, prefix = 'company-bell') {
  // SIEMPRE un botón, NUNCA un `<a href>`, aunque la notificación lleve a algún
  // sitio.
  //
  // Un `href` en el marcado es una segunda vía de navegación que el theme no
  // controla: el navegador se va en cuanto se pulsa y se lleva por delante el
  // marcado a medias. Intentar frenarlo con `preventDefault` es pelearse con el
  // navegador en cada clic, y basta un fallo de JS, una versión vieja del theme
  // o un modificador para que gane el `href` y se navegue sin haber hecho nada.
  //
  // El destino vive en `item.href`, en JavaScript, y solo se usa cuando el
  // servidor confirma. El aspecto de enlace lo pone el CSS.
  const node = el('button', `${prefix}-item`);
  node.type = 'button';
  if (item.href) node.classList.add('is-link');

  if (!item.read) node.classList.add('is-unread');

  const type = item.type || 'info';
  const icono = el('span', `${prefix}-item-icon is-${type}`);
  icono.appendChild(el('i', `fas ${item.icon || ICON_BY_TYPE[type] || ICON_BY_TYPE.info}`));
  node.appendChild(icono);

  const cuerpo = el('span', `${prefix}-item-body`);
  cuerpo.appendChild(el('span', `${prefix}-item-title`, item.title || ''));
  if (item.text) cuerpo.appendChild(el('span', `${prefix}-item-text`, item.text));
  if (item.time) cuerpo.appendChild(el('span', `${prefix}-item-time`, item.time));
  node.appendChild(cuerpo);

  if (onActivate) node.addEventListener('click', (event) => onActivate(item, event, node));

  return node;
}

/**
 * Qué pasa al pulsar una notificación.
 *
 * El orden importa y era el fallo: **primero se cambia el estado en el
 * servidor, y solo si eso sale bien se navega.** Con un `<a href>` el navegador
 * se va inmediatamente, la petición de marcado queda a medias y, si falla,
 * nadie se entera: la notificación sigue sin leer y ya nadie está mirando esa
 * pantalla. Por eso la fila es un botón y aquí no hay ninguna navegación que
 * frenar: la única manera de llegar al destino es pasando por esta función.
 *
 * Mientras dura la petición la fila queda en espera, para que no se pulse dos
 * veces ni parezca que el clic se perdió. Si falla, **no se navega**: se avisa
 * y la notificación se queda como estaba, que es lo honesto.
 *
 * @param {Object} options
 * @param {Object} options.item
 * @param {MouseEvent} options.event
 * @param {HTMLElement} options.node Fila pulsada.
 * @param {string|null} options.readUrl Con `{id}` sustituible.
 * @param {string} [options.readMethod='POST']
 * @param {Function} options.onDone Se llama tras marcarla, antes de navegar.
 * @param {Function} [options.onError]
 * @returns {Promise<boolean>} Si se marcó.
 */
export function activateNotification({
  item,
  event,
  node,
  readUrl,
  readMethod = 'POST',
  onDone,
  onError,
}) {
  // Un botón no navega por su cuenta, así que no hay nada que interceptar. Se
  // frena de todas formas por si la fila acabó dentro de un enlace o de un
  // formulario de la aplicación.
  if (event) event.preventDefault();

  const ir = () => {
    if (item.href) window.location.href = item.href;
  };

  // Ya leída: no hay nada que cambiar, se navega directo.
  if (item.read) {
    ir();
    return Promise.resolve(false);
  }

  // Sin endpoint el theme no inventa una petición: marca en memoria, avisa a la
  // aplicación y navega. Es el modo para quien ya persiste por su cuenta.
  if (!readUrl) {
    if (onDone) onDone(item);
    ir();
    return Promise.resolve(true);
  }

  if (node) node.classList.add('is-pending');

  const url = readUrl.replace('{id}', encodeURIComponent(item.id));

  return fetch(url, {
    method: readMethod,
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'same-origin',
  })
    .then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      // Un 204 no trae cuerpo, y pedirle `json()` reventaría.
      return response.status === 204 ? { ok: true } : response.json().catch(() => ({ ok: true }));
    })
    .then((json) => {
      // El servidor puede responder 200 y aun así decir que no se pudo.
      if (json && json.ok === false) throw new Error(json.error || 'no actualizada');

      if (node) node.classList.remove('is-pending');
      if (onDone) onDone(item, json);

      ir();
      return true;
    })
    .catch((error) => {
      if (node) node.classList.remove('is-pending');
      // NO se navega: el estado no cambió, y llevarse al usuario a otra
      // pantalla escondería que la notificación sigue pendiente.
      if (onError) onError(error, item);
      return false;
    });
}

class NotificationBell {
  constructor(options = {}) {
    this.options = options;
    this.items = (options.items || []).slice();
    this.id = `bell-${Math.random().toString(36).slice(2, 8)}`;

    this._build();
    this._render();
  }

  /** Cuántas quedan sin leer. Es lo que se enseña en el punto. */
  get unread() {
    return this.items.filter((item) => !item.read).length;
  }

  _build() {
    this.root = el('li', 'nav-item dropdown company-bell');

    this.toggle = el('a', 'nav-link company-bell-toggle');
    this.toggle.href = '#';
    this.toggle.id = this.id;
    this.toggle.setAttribute('data-mdb-dropdown-init', '');
    this.toggle.setAttribute('data-mdb-toggle', 'dropdown');
    this.toggle.setAttribute('aria-expanded', 'false');
    this.toggle.setAttribute('aria-label', notificationBellES.open);

    this.toggle.appendChild(el('i', 'fas fa-bell'));

    // El punto con el número. Va oculto sin pendientes: un cero permanente
    // enseña a ignorarlo, y entonces tampoco se ve cuando sí hay algo.
    this.badge = el('span', 'badge rounded-pill badge-notification company-bell-badge');
    this.badge.hidden = true;
    this.toggle.appendChild(this.badge);

    this.root.appendChild(this.toggle);

    this.panel = el('div', 'dropdown-menu dropdown-menu-end company-bell-panel');
    this.panel.setAttribute('aria-labelledby', this.id);

    const header = el('div', 'company-bell-header');
    header.appendChild(el('span', 'company-bell-title', this.options.title || notificationBellES.title));

    this.readAll = el('button', 'company-bell-readall', notificationBellES.readAll);
    this.readAll.type = 'button';
    this.readAll.addEventListener('click', (event) => {
      event.stopPropagation(); // no cerrar el panel al marcar
      this.markAllRead();
    });
    header.appendChild(this.readAll);

    this.panel.appendChild(header);

    this.list = el('div', 'company-bell-list');
    this.panel.appendChild(this.list);

    // Pie opcional: «ver todas», que lleva a la pantalla completa de la app.
    if (this.options.allHref) {
      const footer = el('a', 'company-bell-footer', this.options.allText || notificationBellES.seeAll);
      footer.href = this.options.allHref;
      this.panel.appendChild(footer);
    }

    this.root.appendChild(this.panel);

    this.toggle.addEventListener('click', () => {
      if (this.options.onOpen) this.options.onOpen(this.items);
    });
  }

  _render() {
    const pendientes = this.unread;

    this.badge.hidden = pendientes === 0;
    this.badge.textContent = pendientes > 99 ? '99+' : String(pendientes);
    this.toggle.classList.toggle('has-unread', pendientes > 0);
    this.readAll.hidden = pendientes === 0;

    this.list.innerHTML = '';

    if (!this.items.length) {
      const vacio = el('p', 'company-bell-empty', this.options.emptyText || notificationBellES.empty);
      this.list.appendChild(vacio);
      return;
    }

    this.items.forEach((item) => this.list.appendChild(this._item(item)));
  }

  _item(item) {
    return renderNotification(item, (i, event, node) =>
      activateNotification({
        item: i,
        event,
        node,
        readUrl: this.options.readUrl,
        readMethod: this.options.readMethod,
        onDone: (marcada) => this.markRead(marcada),
        onError: (error) => this._readFailed(error, i),
      })
    );
  }

  /** El marcado falló: no se navegó, así que hay que decirlo. */
  _readFailed(error, item) {
    if (this.options.onReadError) {
      this.options.onReadError(error, item);
      return;
    }

    this.root.dispatchEvent(
      new CustomEvent('readError.company.bell', { bubbles: true, detail: { error, item } })
    );

    if (typeof console !== 'undefined') {
      console.warn('company-theme: no se pudo marcar la notificación como leída', error);
    }
  }

  // ---------------------------------------------------------------------------
  // API para la aplicación
  // ---------------------------------------------------------------------------

  /**
   * Añade una notificación recién llegada. Va la primera: lo último es lo que
   * se mira.
   *
   * @param {Object} item `{ id, title, text, time, icon, href }`
   */
  add(item) {
    this.items.unshift({ read: false, ...item });
    this._render();

    // Un cambio silencioso en un número pequeño pasa desapercibido. La clase
    // dispara la animación del punto; se quita al terminar para poder repetirla.
    this.badge.classList.remove('is-new');
    void this.badge.offsetWidth; // fuerza el reinicio de la animación
    this.badge.classList.add('is-new');

    this.root.dispatchEvent(
      new CustomEvent('added.company.bell', { bubbles: true, detail: { item, unread: this.unread } })
    );

    return this;
  }

  /** Reemplaza la lista entera. */
  setItems(items = []) {
    this.items = items.slice();
    this._render();
    return this;
  }

  markRead(item) {
    if (!item || item.read) return this;

    item.read = true;
    this._render();

    if (this.options.onRead) this.options.onRead(item);
    this.root.dispatchEvent(
      new CustomEvent('read.company.bell', { bubbles: true, detail: { item, unread: this.unread } })
    );

    return this;
  }

  markAllRead() {
    if (!this.unread) return this;

    this.items.forEach((item) => {
      item.read = true;
    });
    this._render();

    if (this.options.onReadAll) this.options.onReadAll();
    this.root.dispatchEvent(
      new CustomEvent('readAll.company.bell', { bubbles: true, detail: { unread: 0 } })
    );

    return this;
  }

  /** Filtra por texto, sin tildes, como el resto de búsquedas del theme. */
  find(text) {
    const term = normalizeText(text);
    return this.items.filter((item) =>
      normalizeText(`${item.title || ''} ${item.text || ''}`).includes(term)
    );
  }

  dispose() {
    this.root.remove();
  }
}

/**
 * Monta la campana dentro del contenedor de acciones de la barra superior.
 *
 * @param {string|HTMLElement} container
 * @param {Object} options
 * @returns {NotificationBell}
 */
export function mountNotificationBell(container, options = {}) {
  const target = typeof container === 'string' ? document.querySelector(container) : container;
  if (!target) return null;

  const bell = new NotificationBell(options);
  target.appendChild(bell.root);

  // El desplegable es markup recién creado: sin esto no se abre al pulsarlo.
  initMdbOn(bell.root, mdbComponents || {});

  target.__companyBell = bell;
  return bell;
}

export { NotificationBell };
